import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    const select = (selector) => this.element.querySelector(selector)

    this.chatRoomInput = select("#chat-room-id")
    this.formUrlInput = select("#form-url")
    this.agentFormUrlInput = select("#agent-form-url")
    this.appSchemeInput = select("#app-scheme")
    this.androidPackageInput = select("#android-package")
    this.navbarSelect = select("#navbar-param")
    this.scrollableSelect = select("#scrollable-param")
    this.rawParamsInput = select("#raw-params-input")
    this.applyRawButton = select("#apply-raw-params")
    this.setNowButton = select("#set-now")
    this.setDisputeStartNowButton = select("#set-dispute-start-now")
    this.generateButton = select("#generate-link")
    this.resetButton = select("#reset-inputs")
    this.webOutput = select("#web-url-output")
    this.deepOutput = select("#deep-link-output")
    this.intentOutput = select("#intent-link-output")
    this.agentWebOutput = select("#agent-web-output")
    this.agentDeepOutput = select("#agent-deep-output")
    this.openWeb = select("#open-web-url")
    this.openDeep = select("#open-deep-link")
    this.openIntent = select("#open-intent-link")
    this.openAgentWeb = select("#open-agent-web")
    this.openAgentDeep = select("#open-agent-deep")
    this.copyStatus = select("#copy-status")

    if (!this.chatRoomInput || !this.formUrlInput || !this.appSchemeInput) {
      return
    }

    this.prefillInputs = Array.from(this.element.querySelectorAll("[data-prefill-key]"))
    this.pageParamKeys = [
      "chat_room_id",
      "android_package",
      "navbar",
      "scrollable",
      ...this.prefillInputs.map((input) => input.dataset.prefillKey).filter(Boolean)
    ]

    this.boundBuildUrls = this.buildUrls.bind(this)

    this.element.querySelectorAll(".prefill-lab__chat-item").forEach((button) => {
      button.addEventListener("click", () => {
        if (!button.dataset.chatRoomId) return
        this.chatRoomInput.value = button.dataset.chatRoomId
        this.updateChatSelection(button)
        this.buildUrls()
      })
    })

    if (this.setNowButton) {
      this.setNowButton.addEventListener("click", () => {
        const nowDate = new Date()
        const nowTimestamp = Math.floor(nowDate.getTime() / 1000)
        const nowDatetime = this.formatDateTimeForPrefill(nowDate)
        const target =
          this.element.querySelector('[data-prefill-key="received_datetime"]') ||
          this.element.querySelector('[data-prefill-key="received_timestamp"]')
        if (target) {
          target.value = target.dataset.prefillKey === "received_datetime" ? nowDatetime : nowTimestamp
        }
        this.buildUrls()
      })
    }

    if (this.setDisputeStartNowButton) {
      this.setDisputeStartNowButton.addEventListener("click", () => {
        const nowDate = new Date()
        const nowTimestamp = Math.floor(nowDate.getTime() / 1000)
        const nowDatetime = this.formatDateTimeForPrefill(nowDate)
        const target =
          this.element.querySelector('[data-prefill-key="dispute_start_datetime"]') ||
          this.element.querySelector('[data-prefill-key="dispute_start_timestamp"]')
        if (target) {
          target.value = target.dataset.prefillKey === "dispute_start_datetime" ? nowDatetime : nowTimestamp
        }
        this.buildUrls()
      })
    }

    if (this.applyRawButton) {
      this.applyRawButton.addEventListener("click", () => this.applyRawParams())
    }
    if (this.rawParamsInput) {
      this.rawParamsInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          this.applyRawParams()
        }
      })
    }

    if (this.generateButton) {
      this.generateButton.addEventListener("click", this.boundBuildUrls)
    }
    if (this.resetButton) {
      this.resetButton.addEventListener("click", () => this.resetInputs())
    }

    this.element.querySelectorAll("[data-copy-target]").forEach((button) => {
      button.addEventListener("click", () => {
        let target = this.webOutput ? this.webOutput.value : ""
        if (button.dataset.copyTarget === "deep") {
          target = this.deepOutput ? this.deepOutput.value : ""
        } else if (button.dataset.copyTarget === "intent") {
          target = this.intentOutput ? this.intentOutput.value : ""
        } else if (button.dataset.copyTarget === "agent-web") {
          target = this.agentWebOutput ? this.agentWebOutput.value : ""
        } else if (button.dataset.copyTarget === "agent-deep") {
          target = this.agentDeepOutput ? this.agentDeepOutput.value : ""
        }
        this.copyText(target)
      })
    })

    const autoInputs = [
      this.chatRoomInput,
      this.formUrlInput,
      this.agentFormUrlInput,
      this.appSchemeInput,
      this.androidPackageInput,
      this.navbarSelect,
      this.scrollableSelect,
      ...this.prefillInputs
    ].filter(Boolean)

    autoInputs.forEach((input) => {
      input.addEventListener("input", this.boundBuildUrls)
      input.addEventListener("change", this.boundBuildUrls)
    })

    const openThisPageDeeplinkButton = select("#open-this-page-deeplink")
    if (openThisPageDeeplinkButton) {
      openThisPageDeeplinkButton.addEventListener("click", () => {
        const appScheme = this.appSchemeInput ? this.appSchemeInput.value.trim() : ""
        if (!appScheme) {
          this.setCopyStatus("App Scheme을 먼저 입력해주세요.")
          return
        }
        const currentUrl = window.location.href
        const deepLink = `${appScheme}?remote=${encodeURIComponent(currentUrl)}`
        window.location.href = deepLink
      })
    }

    this.applyQueryToInputs()
    this.buildUrls()
  }

  setCopyStatus(message) {
    if (!this.copyStatus) return
    this.copyStatus.textContent = message
    if (!message) return
    setTimeout(() => {
      this.copyStatus.textContent = ""
    }, 2000)
  }

  formatDateTimeForPrefill(date) {
    const pad = (value) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  }

  updateChatSelection(selectedButton) {
    this.element.querySelectorAll(".prefill-lab__chat-item").forEach((button) => {
      button.classList.toggle("is-selected", button === selectedButton)
    })
  }

  buildUrls() {
    if (!this.formUrlInput || !this.webOutput || !this.deepOutput || !this.intentOutput) {
      return
    }

    const baseFormUrl = this.formUrlInput.value.trim()
    const agentBaseUrl = this.agentFormUrlInput ? this.agentFormUrlInput.value.trim() : ""
    const chatRoomId = this.chatRoomInput ? this.chatRoomInput.value.trim() : ""

    if (!baseFormUrl) {
      this.setCopyStatus("Form URL을 입력해주세요.")
      return
    }

    let formUrl
    try {
      formUrl = new URL(baseFormUrl)
    } catch (error) {
      this.setCopyStatus("Form URL 형식이 올바르지 않습니다.")
      return
    }

    if (chatRoomId) {
      formUrl.searchParams.set("chat_room_id", chatRoomId)
    }

    this.prefillInputs.forEach((input) => {
      const key = input.dataset.prefillKey
      const value = (input.value || "").trim()
      if (value) {
        formUrl.searchParams.set(key, value)
      } else {
        formUrl.searchParams.delete(key)
      }
    })

    const webUrl = formUrl.toString()
    const appScheme = this.appSchemeInput ? this.appSchemeInput.value.trim() : ""
    const navbarValue = this.navbarSelect ? this.navbarSelect.value : ""
    const scrollableValue = this.scrollableSelect ? this.scrollableSelect.value : ""

    let deepLink = appScheme ? `${appScheme}?remote=${encodeURIComponent(webUrl)}` : webUrl
    if (appScheme && navbarValue) {
      deepLink += `&navbar=${encodeURIComponent(navbarValue)}`
    }
    if (appScheme && scrollableValue) {
      deepLink += `&scrollable=${encodeURIComponent(scrollableValue)}`
    }

    let intentLink = ""
    if (appScheme) {
      const schemeSeparator = "://"
      const schemeIndex = appScheme.indexOf(schemeSeparator)
      const schemeName = schemeIndex >= 0 ? appScheme.slice(0, schemeIndex) : ""
      let intentPath = deepLink
      if (schemeName && deepLink.startsWith(`${schemeName}://`)) {
        intentPath = deepLink.slice(schemeName.length + schemeSeparator.length)
      }
      const packageName = this.androidPackageInput ? this.androidPackageInput.value.trim() : ""
      if (schemeName) {
        const packageSegment = packageName ? `;package=${packageName}` : ""
        intentLink = `intent://${intentPath}#Intent;scheme=${schemeName}${packageSegment};end`
      }
    }

    this.webOutput.value = webUrl
    this.deepOutput.value = deepLink
    this.intentOutput.value = intentLink
    if (this.openWeb) this.openWeb.href = webUrl
    if (this.openDeep) this.openDeep.href = deepLink
    if (this.openIntent) this.openIntent.href = intentLink || "#"

    let agentWebUrl = ""
    let agentDeepLink = ""
    if (agentBaseUrl && chatRoomId) {
      try {
        const agentUrl = new URL(agentBaseUrl)
        agentUrl.searchParams.set("channel_id", chatRoomId)
        agentUrl.searchParams.set("referrer", "prefill_lab")
        agentWebUrl = agentUrl.toString()
        const agentScheme = appScheme || "karrot.alpha://minikarrot/router"
        agentDeepLink = `${agentScheme}?remote=${encodeURIComponent(agentWebUrl)}&navbar=false&scrollable=true`
      } catch (error) {
        agentWebUrl = ""
        agentDeepLink = ""
      }
    }

    if (this.agentWebOutput) {
      this.agentWebOutput.value = agentWebUrl
    }
    if (this.agentDeepOutput) {
      this.agentDeepOutput.value = agentDeepLink
    }
    if (this.openAgentWeb) {
      this.openAgentWeb.href = agentWebUrl || "#"
    }
    if (this.openAgentDeep) {
      this.openAgentDeep.href = agentDeepLink || "#"
    }

    this.syncPageQuery()
  }

  applyQueryToInputs() {
    const params = new URLSearchParams(window.location.search)

    const chatRoomParam = params.get("chat_room_id")
    if (chatRoomParam && this.chatRoomInput) {
      this.chatRoomInput.value = chatRoomParam
      const matched = this.element.querySelector(`.prefill-lab__chat-item[data-chat-room-id="${chatRoomParam}"]`)
      if (matched) this.updateChatSelection(matched)
    }

    const androidPackageParam = params.get("android_package")
    if (androidPackageParam && this.androidPackageInput) this.androidPackageInput.value = androidPackageParam

    const navbarParam = params.get("navbar")
    if (navbarParam !== null && this.navbarSelect) this.navbarSelect.value = navbarParam
    const scrollableParam = params.get("scrollable")
    if (scrollableParam !== null && this.scrollableSelect) this.scrollableSelect.value = scrollableParam

    this.prefillInputs.forEach((input) => {
      const key = input.dataset.prefillKey
      const value = params.get(key)
      if (value !== null) input.value = value
    })
  }

  syncPageQuery() {
    const params = new URLSearchParams(window.location.search)
    this.pageParamKeys.forEach((key) => params.delete(key))

    const chatRoomId = this.chatRoomInput ? this.chatRoomInput.value.trim() : ""
    if (chatRoomId) params.set("chat_room_id", chatRoomId)

    const androidPackageValue = this.androidPackageInput ? this.androidPackageInput.value.trim() : ""
    if (androidPackageValue) params.set("android_package", androidPackageValue)

    const navbarValue = this.navbarSelect ? this.navbarSelect.value : ""
    if (navbarValue) {
      params.set("navbar", navbarValue)
    } else {
      params.delete("navbar")
    }

    const scrollableValue = this.scrollableSelect ? this.scrollableSelect.value : ""
    if (scrollableValue) {
      params.set("scrollable", scrollableValue)
    } else {
      params.delete("scrollable")
    }

    this.prefillInputs.forEach((input) => {
      const key = input.dataset.prefillKey
      const value = (input.value || "").trim()
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    })

    const queryString = params.toString()
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    window.history.replaceState(null, "", nextUrl)
  }

  async copyText(text) {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      this.setCopyStatus("복사되었습니다.")
    } catch (error) {
      const temp = document.createElement("textarea")
      temp.value = text
      document.body.appendChild(temp)
      temp.select()
      document.execCommand("copy")
      document.body.removeChild(temp)
      this.setCopyStatus("복사되었습니다.")
    }
  }

  normalizeParamString(raw) {
    let rawString = raw.trim()
    if (!rawString) return ""

    if (rawString.includes("remote=")) {
      try {
        const url = new URL(rawString)
        const remoteValue = url.searchParams.get("remote")
        if (remoteValue) {
          rawString = decodeURIComponent(remoteValue)
        }
      } catch (error) {
        const match = rawString.match(/remote=([^&]+)/)
        if (match && match[1]) {
          rawString = decodeURIComponent(match[1])
        }
      }
    }

    try {
      if (rawString.startsWith("http")) {
        const url = new URL(rawString)
        rawString = url.search
      }
    } catch (error) {
      // ignore
    }

    if (rawString.startsWith("?")) rawString = rawString.slice(1)

    let decoded = rawString
    try {
      decoded = decodeURIComponent(rawString)
    } catch (error) {
      decoded = rawString
    }

    if (decoded.startsWith("&")) decoded = decoded.slice(1)
    if (decoded.includes("?")) decoded = decoded.split("?")[1]

    return decoded
  }

  applyRawParams() {
    if (!this.rawParamsInput) return
    const rawValue = this.rawParamsInput.value
    if (!rawValue) return
    const normalized = this.normalizeParamString(rawValue)
    if (!normalized) return

    const params = new URLSearchParams(normalized)
    params.forEach((value, key) => {
      if (key === "chat_room_id") {
        if (this.chatRoomInput) this.chatRoomInput.value = value
        const matched = this.element.querySelector(`.prefill-lab__chat-item[data-chat-room-id="${value}"]`)
        if (matched) this.updateChatSelection(matched)
        return
      }
      if (key === "navbar") {
        if (this.navbarSelect) this.navbarSelect.value = value
        return
      }
      if (key === "scrollable") {
        if (this.scrollableSelect) this.scrollableSelect.value = value
        return
      }
      const target = this.prefillInputs.find((input) => input.dataset.prefillKey === key)
      if (target) {
        target.value = value
      }
    })
    this.buildUrls()
  }

  resetInputs() {
    if (this.chatRoomInput) this.chatRoomInput.value = ""
    this.element.querySelectorAll(".prefill-lab__chat-item").forEach((button) => {
      button.classList.remove("is-selected")
    })
    this.prefillInputs.forEach((input) => {
      input.value = ""
    })
    if (this.rawParamsInput) this.rawParamsInput.value = ""
    if (this.navbarSelect) this.navbarSelect.value = "false"
    if (this.scrollableSelect) this.scrollableSelect.value = "false"
    if (this.agentFormUrlInput) {
      this.agentFormUrlInput.value = "https://cs-agent.alpha.kr.karrotwebview.com/conflict-resolution/"
    }
    this.buildUrls()
  }
}
